import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { ButtonSubmit } from '../components/form/button/Button';
import { FormAddProductFacade } from '../components/form/form_add_product_facade';
import { FormAddProductPlan } from '../components/form/form_add_product_plan';
import { FormPanel } from '../components/form/form_panel';
import { FormValidationMessage } from '../components/form/form_validation_message';
import { Checkboxes } from '../components/form/input/checkboxes';
import { InputPanel } from '../components/form/input/input_panel';
import { Label } from '../components/form/input/Label';
import { Radioboxes } from '../components/form/input/radioboxes';
import { LabelValidation } from '../components/form/label_validation';
import IconAdd from '../components/icon/IconAdd';
import Popup from '../components/popup/Popup';
import { getBaseURL } from '../config/runtime';
import { Locations } from '../domain/client/locations';
import { Facades } from '../domain/product/facades';
import { Plan } from '../domain/product/plan';
import { Plans } from '../domain/product/Plans';
import { Product } from '../domain/product/product';
import { ErrorResponse } from '../error/error';
import { fetchJson, getResolveJSON } from '../util/fetch';

interface Props {
  proxyProduct: Product;
  onProxyProductAdded: (product: Product) => void;
  onProxyProductAddError: (errorResponse: ErrorResponse) => void;
}

export const FormAddProxyProduct = (props: Props) => {
  const [facades, setFacades] = React.useState<Facades | null>(null);
  const [plans, setPlans] = React.useState<Plans | null>(null);
  const [locations, setLocations] = React.useState<Locations | null>(null);

  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedFacade, setSelectedFacade] = useState<string>('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOrientation, setSelectedOrientation] = useState<string>('');

  const [showFormValidation, setShowFormValidation] = useState(false);
  const [formValidationMessage, setFormValidationMessage] = useState('');

  const [showFacadeValidation, setShowFacadeValidation] = useState(false);
  const [showPlanValidation, setShowPlanValidation] = useState(false);
  const [showLocationValidation, setShowLocationValidation] = useState(false);
  const [showOrientationValidation, setShowOrientationValidation] =
    useState(false);

  const [loading, setLoading] = useState(false);

  const firstUpdate = useRef(true);

  const validatePlan = () => {
    const validPlan = selectedPlan?.length > 0;
    setShowPlanValidation(!validPlan);
    return validPlan;
  };

  const validateFacadeName = () => {
    const validFacade = selectedFacade?.length > 0;
    setShowFacadeValidation(!validFacade);
    return validFacade;
  };

  const validateLocations = () => {
    const validLocations = selectedLocations?.length > 0;
    setShowLocationValidation(!validLocations);
    return validLocations;
  };

  const validateOrientation = () => {
    const validOrientation = selectedOrientation?.length > 0;
    setShowOrientationValidation(!validOrientation);
    return validOrientation;
  };

  useEffect(() => {
    if (!firstUpdate.current) {
      validatePlan();
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (!firstUpdate.current) {
      validateFacadeName();
    }
  }, [selectedFacade]);

  useEffect(() => {
    if (!firstUpdate.current) {
      validateLocations();
    }
  }, [selectedLocations]);

  useEffect(() => {
    if (!firstUpdate.current) {
      validateOrientation();
    }
  }, [selectedOrientation]);

  useEffect(() => {
    firstUpdate.current = false;
  }, []);

  const fetchData = React.useCallback(async () => {
    const [facadesLocal, plansLocal, locationsLocal] = await Promise.all([
      getResolveJSON<Facades>(`${getBaseURL()}/v1/client/facades`),
      getResolveJSON<Plans>(`${getBaseURL()}/v1/client/plans`),
      getResolveJSON<Locations>(`${getBaseURL()}/v1/client/locations`),
    ]);

    setFacades(facadesLocal);
    setPlans(plansLocal);
    setLocations(locationsLocal);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const onPlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPlan(e.target.value);
  };

  const onFacadeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFacade(e.target.value);
  };

  const addProxyProduct = async () => {
    setLoading(true);
    const respPromise = fetchJson<
      (Product & { object: string }) | ErrorResponse
    >({
      url: `${getBaseURL()}/v1/client/proxy-products`,
      options: {
        method: 'POST',
        body: JSON.stringify({
          proxy_product_id: props.proxyProduct.id,
          plan_id: selectedPlan,
          facade_name: selectedFacade,
          locations: selectedLocations,
          orientation_default: selectedOrientation,
          active: false,
        }),
      },
    });

    respPromise
      .then((resp) => {
        setLoading(false);
        if (resp.object === 'product' || resp.object === 'products') {
          props.onProxyProductAdded(resp);
        } else {
          const error = resp as ErrorResponse;
          const message = error?.message.includes('Duplicate')
            ? 'A linked clone already exists with this plan/facade combination.'
            : 'An unknown error has occurred.';
          setFormValidationMessage(message);
          setShowFormValidation(true);
          props.onProxyProductAddError(resp as ErrorResponse);
        }
      })
      .catch((e) => {
        setLoading(false);
        const errorMessage =
          e.response?.status === 409
            ? 'A linked clone already exists with this plan/facade combination.'
            : 'An unknown error has occurred.';

        setFormValidationMessage(errorMessage);
        setShowFormValidation(true);
        props.onProxyProductAddError({
          object: 'error',
          message: errorMessage,
        } as ErrorResponse);
      })
      .finally(() => setLoading(false));
  };

  const validate = () => {
    const validPlan = validatePlan();
    const validFacadeName = validateFacadeName();
    const validLocations = validateLocations();
    const validOrientation = validateOrientation();
    return validPlan && validFacadeName && validLocations && validOrientation;
  };

  const submit = () => {
    if (!loading && validate()) {
      addProxyProduct();
    }
  };

  const getExistingFacadeName = (facadeName: string) => {
    return (
      facades?.data?.find(
        (facade) => facade.name.toLowerCase() === facadeName.toLowerCase()
      )?.name ?? ''
    );
  };

  return (
    <FormPanel style={{ width: '25rem' }}>
      <FormValidationMessage
        style={{
          display: showFormValidation ? 'block' : 'none',
          marginBottom: '1rem',
        }}
        text={formValidationMessage}
      />

      <Label>New house plan</Label>
      <InputPanel style={{ marginBottom: '.15rem' }}>
        <select
          disabled={loading}
          id="plans"
          onChange={onPlanChange}
          value={selectedPlan}
          style={{ width: '15rem' }}
        >
          <option key="select" value="none">
            Select...
          </option>
          {plans?.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
        <IconAdd
          onClick={() => {
            if (loading) {
              return;
            }

            const popup = ReactDOM.createRoot(
              document.getElementById('popup') as HTMLElement
            );
            popup.render(
              <Popup
                onClose={() => {
                  popup.unmount();
                }}
                title="Add Plan"
                zIndex={20}
              >
                <FormAddProductPlan
                  onPlanSelected={(plan: Plan) => {
                    popup.unmount();
                    plans?.data.unshift(plan);
                    setSelectedPlan(plan.id);
                  }}
                />
              </Popup>
            );
          }}
          style={{
            height: '1.5rem',
            width: '1.5rem',
            fill: 'green',
            marginLeft: '.75rem',
            opacity: loading ? 0.3 : 1,
          }}
        />
      </InputPanel>
      <LabelValidation
        show={showPlanValidation}
        style={{ marginBottom: '.5rem' }}
      >
        Please select a plan.
      </LabelValidation>

      <Label>New house facade</Label>
      <InputPanel style={{ marginBottom: '.15rem' }}>
        <select
          disabled={loading}
          id="facades"
          onChange={onFacadeChange}
          value={selectedFacade}
          style={{ width: '15rem' }}
        >
          <option key="select">Select...</option>
          {facades?.data.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </select>
        <IconAdd
          onClick={() => {
            if (loading) {
              return;
            }

            const popup = ReactDOM.createRoot(
              document.getElementById('popup') as HTMLElement
            );
            popup.render(
              <Popup
                onClose={() => {
                  popup.unmount();
                }}
                title="Add Facade"
                zIndex={20}
              >
                <FormAddProductFacade
                  onFacadeNameSelected={(facadeName: string) => {
                    popup.unmount();
                    const existingFacadeName =
                      getExistingFacadeName(facadeName);
                    const newFacadeName = existingFacadeName
                      ? existingFacadeName
                      : facadeName;
                    if (!existingFacadeName) {
                      facades?.data.unshift({ name: newFacadeName });
                    }
                    setSelectedFacade(newFacadeName);
                  }}
                />
              </Popup>
            );
          }}
          style={{
            height: '1.5rem',
            width: '1.5rem',
            fill: 'green',
            marginLeft: '.75rem',
            opacity: loading ? 0.3 : 1,
          }}
        />
      </InputPanel>
      <LabelValidation
        show={showFacadeValidation}
        style={{ marginBottom: '.5rem' }}
      >
        Please select a facade.
      </LabelValidation>

      <Label>Choose available locations</Label>
      <Checkboxes
        data={locations?.data.map((l) => ({ label: l.id, value: l.id })) ?? []}
        disabled={loading}
        onChange={(locs) => setSelectedLocations(locs)}
        style={{ marginBottom: '.15rem' }}
      />
      <LabelValidation
        show={showLocationValidation}
        style={{ marginBottom: '.5rem' }}
      >
        Please select 1 or more locations.
      </LabelValidation>

      <Label>Default orientation (garage position)</Label>
      <Radioboxes
        data={[
          { label: 'Right', value: 'RIGHT' },
          { label: 'Left', value: 'LEFT' },
        ]}
        disabled={loading}
        groupName="orientation"
        onChange={(value: string) => setSelectedOrientation(value)}
        style={{ marginBottom: '.15rem' }}
      />
      <LabelValidation
        show={showOrientationValidation}
        style={{ marginBottom: '1rem' }}
      >
        Please select an orientation.
      </LabelValidation>

      <ButtonSubmit disabled={loading} onClick={() => submit()} text="Save" />
    </FormPanel>
  );
};
